import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { BackButton, Image, LoaderContainer, YouTubeScreen } from "./publish-component";
import { API, API_URL, STATIC_PATH } from "../constants/config";
import Typography from "../common-components/Typography";
import content from "../constants/content";
import { font } from "../constants/font";
import Channel from "./channel";
import useApi from "../helper/hooks/useApi";
import Spinner, { SPINNER_TYPE } from "../common-components/Spinner";
import useNotify from "../helper/hooks/useNotify";
import useExport from "../helper/hooks/useExport";
import ExportDetails from "./export-details";

const Instagram = ({ onBackAction, isShortsPlayer, shortId, showWatermMark }) => {
    const { post } = useApi();
    const { success, warn } = useNotify();
    const userDetails = useSelector((store) => store.userDetails);
    const { userId, teamId } = userDetails;
    const { triggerVC } = useExport();
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [channelList, setchannelList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const loadChannelData = useCallback(() => {
        setIsLoading(true);

        const payload = { userId, teamId };
        post(`${API_URL}${API.INSTAGRAM.GET_ACCOUNTS}`, payload).then((res) => {
            if (!res.error) {
                setchannelList(res.result);
            }
            setIsLoading(false);
        }).catch((error) => {
            warn(content.SOMETHING_WENT_WRONG);
            console.error(error);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, teamId]);


    const checkChannelStatus = async (retryCount = 0, maxRetries = 30) => {
        try {
            const res = await post(`${API_URL}${API.INSTAGRAM.GET_CHANNEL_STATUS}`, {}, false);
            if (res.status === 'success' || res.status === 'none') {
                loadChannelData();
                return true;
            }

            if (retryCount < maxRetries) {
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve(checkChannelStatus(retryCount + 1, maxRetries));
                    }, 3000);
                });
            }

            return false;
        } catch (error) {
            if (retryCount < maxRetries) {
                return new Promise(resolve => {
                    setTimeout(() => {
                        resolve(checkChannelStatus(retryCount + 1, maxRetries));
                    }, 2000);
                });
            }
            return false;
        }
    };

    const openChannelHandler = (url) => {
        return new Promise((resolve) => {
            const win = window.open(url, "TikTok", "width=500, height=600");

            if (!win) {
                resolve({ error: true, errorMsg: "Popup was blocked." });
                return;
            }

            checkChannelStatus().then(success => {
                if (success) {
                    if (win && !win.closed) {
                        win.close();
                    }
                    resolve({ error: false, errorMsg: "" });
                } else {
                    resolve({ error: true, errorMsg: "Authentication failed." });
                }
            });
        });
    }

    const handleAddChannel = () => {
        return openChannelHandler(`${API_URL}${API.INSTAGRAM.ADD_CHANNELS}`);
    };

    const onRefresh = (channelId) => {
        return openChannelHandler(`${API_URL}${API.INSTAGRAM.ADD_CHANNELS}?channel_id=${channelId}`);
    };


    const handleChannelDelete = (value) => {
        setIsDeleting(true);
        const payload = {
            userId,
            socialId: value.socialId,
        };
        post(`${API_URL}${API.INSTAGRAM.CHANNEL_DELETE}`, payload).then((res) => {
            if (!res.error) {
                success("Channel deleted successfully");
                loadChannelData();
            } else {
                warn("Failed to delete channel");
            }
            setIsDeleting(false);
        }).catch((error) => {
            console.error("Error deleting TikTok channel:", error);
            warn("Failed to delete channel");
            setIsDeleting(false);
        });
    };

    const handleChannelClick = (channel) => {
        setSelectedChannel(channel);
        triggerVC(true);
    };

    const onBack = () => {
        if (selectedChannel) {
            setSelectedChannel(null);
        } else {
            onBackAction();
        }
    }

    useEffect(() => {
        loadChannelData();
    }, [loadChannelData]);

    return isLoading ?
        <LoaderContainer>
            <Spinner type={SPINNER_TYPE.DOTTED_SPINNER} />
        </LoaderContainer> :
        <YouTubeScreen>
            <BackButton>
                <Image
                    src={`${STATIC_PATH}back-arrow.svg`}
                    width="20px"
                    height="20px"
                    alt="back"
                    className="back-button"
                    onClick={onBack}
                />
                <Image
                    src={`${STATIC_PATH}instagram.svg`}
                    width="24px"
                    height="24px"
                    alt="instagram"
                />
                <Typography
                    content={content.INSTAGRAM}
                    font={font.boldRegular_25}
                />
            </BackButton>
            {!selectedChannel && (
                <Channel
                    channelList={channelList}
                    onChannelClick={handleChannelClick}
                    deleteChannel={handleChannelDelete}
                    addChannel={handleAddChannel}
                    onRefresh={onRefresh}
                    isDeleting={isDeleting}
                />
            )}
            {selectedChannel && (
                <ExportDetails
                    isInstagram
                    goBack={onBack}
                    selectedChannel={selectedChannel}
                    isShortsPlayer={isShortsPlayer}
                    shortId={shortId}
                    showWatermMark={showWatermMark}
                />
            )}
        </YouTubeScreen>

};

Instagram.propTypes = {
    onBackAction: PropTypes.func,
    isShortsPlayer: PropTypes.bool,
    shortId: PropTypes.string,
    showWatermMark: PropTypes.bool
};

export default Instagram;
